import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import { ClubSearchShort } from '@interlayer/js/app/components/main/ClubSearch/components/ClubSearchShort/ClubSearchShort';
import { NearestClub } from '@interlayer/js/app/components/main/ClubSearch/components/NearestClub/NearestClub';
import { Button } from '@interlayer/js/components/common/Button';
import { I18n } from '@interlayer/js/components/common/I18n';

import { useTranslations } from '@core/js/hooks/i18n';
import { useMountEffect } from '@core/js/hooks/useMountEffect';
import { useUserSelector } from '@core/js/store/hooks';

import { useNearestClub } from '@interlayer/js/app/hooks/postCodeFinder/hooks/useNearestClub';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { setClubData } from '@interlayer/js/app/store/reducers/postCodeFinder/actions';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useRunBrowserGeolocation } from '@interlayer/js/components/tagTypes/GeoComply/hooks/useRunBrowserGeolocation';
import { useNavigate } from '@interlayer/js/hooks/navigate';

import { TClubSearchProps } from './types';

import style from './ClubSearch.scss';

export const ClubSearch: React.FC<TClubSearchProps> = ({ config: { isShowNearestClubPanel } = {} }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const t = useTranslations('playtech.mobile.system.club.');
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const zip = useUserSelector((state) => state.userDetails.zip);
    const [isUserLocationBlocked, setUserLocationBlocked] = useState(false);
    const [isLoadingNearestSearch, setIsLoadingNearestSearch] = useState(false);
    const [dTagClubsSearchResultUrl = '/clubs/search-results'] = useDynamicTags([DYNAMIC_TAG_NAMES.clubsSearchResultUrl]);
    const runBrowserGeolocation = useRunBrowserGeolocation({ isMandatory: false, isSilent: true });
    const { clubsNearest, getNearestClubs } = useNearestClub();

    const handlerErrorBrowserGeolocation = useCallback(() => {
        return getNearestClubs(zip)
            .catch(() => setUserLocationBlocked(true))
            .finally(() => setIsLoadingNearestSearch(false));
    }, [getNearestClubs, zip]);

    const getNearestClubData = (isMounted: () => boolean) => {
        setIsLoadingNearestSearch(true);

        runBrowserGeolocation()
            .then(async (response) => {
                const query = response.coords.latitude && response.coords.longitude
                    ? [response.coords.latitude, response.coords.longitude]
                    : '';

                if (isMounted()) {
                    getNearestClubs(query, true);
                }
            })
            .then(() => isMounted() && setIsLoadingNearestSearch(false))
            .catch(() => {
                if (!isMounted()) {
                    return false;
                }

                if (isLoggedIn) {
                    handlerErrorBrowserGeolocation();
                } else {
                    setUserLocationBlocked(true);
                    setIsLoadingNearestSearch(false);
                }
            });
    };

    useMountEffect((isMounted: () => boolean) => {
        if (isShowNearestClubPanel) {
            getNearestClubData(isMounted);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const handlerViewAll = () => {
        dispatch(setClubData({
            name: ''
        }));
        navigate(dTagClubsSearchResultUrl);
    };

    return (
        <div className={style.container}>
            <ClubSearchShort />

            {isShowNearestClubPanel && (
                <div data-testid="ClubSearch_nearestClub" className={clsx(style.nearestClubWrap, style.locationBlock)}>
                    <div className={style.nearestClubBlock}>
                        <I18n
                            t={t}
                            tKey="nearestClubs.title"
                            className={style.nearestClubsTitle}
                        />

                        {Array.isArray(clubsNearest) && Boolean(clubsNearest.length) && !isUserLocationBlocked && (
                            <Button
                                size="medium"
                                view="link"
                                className={style.viewAllLink}
                                onClick={handlerViewAll}
                                data-testid="ClubSearch_viewAllLink"
                            >
                                {t('nearestClubs.viewAll')}
                            </Button>
                        )}
                    </div>

                    <NearestClub
                        nearestClubData={clubsNearest}
                        isUserLocationBlocked={isUserLocationBlocked}
                        isLoadingNearestSearch={isLoadingNearestSearch}
                    />
                </div>
            )}
        </div>
    );
};

export default ClubSearch;
